import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Plus, Printer, Money, X, UploadSimple, FileText, Receipt, WarningCircle, BookOpen, Question, MagnifyingGlass } from "@phosphor-icons/react";
import { printReceipt } from "@/lib/printReceipt";
import { CardTopupModal } from "@/components/RenewTopupModals";
import { PassbookModal, ReportLostModal, InfoModal, TRANSIT_CARD_TERMS, TRANSIT_CARD_FAQ } from "@/components/PassbookAndInfo";

const COUNTERS = ["Margao", "Panaji", "Vasco", "Mapusa"];
const GENDERS = ["Male", "Female", "Other"];

// Category-specific document map. Base uploads (Govt ID photo + Passport photo) apply to all.
const CATEGORY_DOCS = {
  "Student": {
    extras: [
      { field: "institution_id_front_url", label: "School/College ID Proof (Front-side) *" },
      { field: "institution_id_back_url",  label: "School/College ID Proof (Back-side) *"  },
    ],
  },
  "Senior Citizen": {
    extras: [{ field: "senior_citizen_card_url", label: "Senior Citizen Card photo *" }],
  },
  "Person with Disability": { extras: [] },
  "Freedom Fighter":        { extras: [] },
  "General Public":         { extras: [] },
};

export default function SmartTransitCard() {
  const [cards, setCards] = useState([]);
  const [opts, setOpts] = useState({ categories: [], govt_id_types: [], institution_types: [], institutions: [], counters: COUNTERS, card_fee: 100, min_opening_balance: 200 });
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(null);
  const [topupCard, setTopupCard] = useState(null);
  const [passbookCard, setPassbookCard] = useState(null);
  const [lostCard, setLostCard] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [balLookup, setBalLookup] = useState({ q: "", result: null, busy: false });
  const [q, setQ] = useState("");
  const blank = {
    category: "General Public",
    name: "", parent_name: "",
    govt_id_type: "Aadhar Card", govt_id_number: "", govt_id_url: "",
    mobile: "", email: "",
    date_of_birth: "", age: "", gender: "Male",
    address: "", state: "Goa", city: "", pincode: "",
    institution_type: "", institution_name: "", institution_id_front_url: "", institution_id_back_url: "", institution_id_expiry: "",
    senior_citizen_card_url: "", disability_card_url: "", freedom_fighter_cert_url: "",
    photo_url: "",
    counter: "Margao",
    card_fee: 150, opening_balance: 0,
    consent_signed: false,
  };
  const [f, setF] = useState(blank);

  const load = async () => {
    const [o, c] = await Promise.all([
      api.get("/transit-cards/options"),
      api.get("/transit-cards"),
    ]);
    setOpts(o.data); setCards(c.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (f.date_of_birth) {
      const dob = new Date(f.date_of_birth);
      const today = new Date();
      const age = Math.floor((today - dob) / 31557600000);
      if (!isNaN(age) && age >= 0 && age < 130) setF(x => ({ ...x, age: String(age) }));
    }
  }, [f.date_of_birth]);

  const uploadFile = async (field, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploading(field);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF(x => ({ ...x, [field]: data.url }));
      toast.success(`${file.name} uploaded`);
    } catch { toast.error("Upload failed"); }
    setUploading(null);
  };

  const categoryDocs = CATEGORY_DOCS[f.category] || { extras: [] };

  const validate = () => {
    if (!f.name) return "Name is required";
    if (!f.parent_name) return "Father / Husband Name is required";
    if (!f.govt_id_number) return "Government ID Number is required";
    if (!f.mobile || f.mobile.length < 10) return "Valid mobile number is required";
    if (!f.date_of_birth) return "Date of Birth is required";
    if (!f.gender) return "Gender is required";
    if (!f.address) return "Address is required";
    if (!f.city) return "City is required";
    if (!f.pincode) return "Pincode is required";
    if (!f.govt_id_url) return "Government ID photo upload is required";
    if (!f.photo_url) return "Passport-size photograph upload is required";
    for (const d of categoryDocs.extras) {
      if (!f[d.field]) return `${d.label.replace(/ \*$/,"")} upload is required`;
    }
    if (f.category === "Student" && (!f.institution_name || !f.institution_id_expiry))
      return "Institution name and ID expiry date are required";
    if (!f.counter) return "Please select a Counter";
    if (!f.consent_signed) return "Please accept the Self-Declaration";
    return null;
  };

  const issue = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    try {
      const { data } = await api.post("/transit-cards/issue", f);
      setCards(p => [data, ...p]);
      toast.success(`Card ${data.card_no} issued · Total ₹${data.total_paid}`);
      printReceipt({ kind: "card", data: { ...data, holder_name: data.name, card_type: data.category, deposit: data.card_fee, amount: data.opening_balance } });
      setShow(false); setF(blank); setStep(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to issue card");
    }
  };

  const cancelCard = async (c) => {
    if (!window.confirm(`Cancel transit card ${c.card_no} and refund balance ₹${c.balance ?? 0}?`)) return;
    try { const { data } = await api.post(`/transit-cards/${c.id}/cancel`);
      toast.success(`Cancelled · refund ₹${data.refund}`); load();
    } catch { toast.error("Cancel failed"); }
  };

  const filtered = cards.filter(c => q === "" || (c.card_no||"").toLowerCase().includes(q.toLowerCase()) || (c.name||"").toLowerCase().includes(q.toLowerCase()) || (c.mobile||"").includes(q));

  const lookupBalance = async () => {
    const term = balLookup.q.trim();
    if (!term) { toast.error("Enter card number or mobile"); return; }
    setBalLookup(x => ({ ...x, busy: true, result: null }));
    try {
      const qs = /^\d{10}$/.test(term) ? `mobile=${term}` : `card_no=${encodeURIComponent(term)}`;
      const { data } = await api.get(`/transit-cards/balance?${qs}`);
      setBalLookup(x => ({ ...x, result: data, busy: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Card not found");
      setBalLookup(x => ({ ...x, busy: false }));
    }
  };

  const counts = opts.categories.map(cat => ({ cat, count: cards.filter(c => c.category === cat).length }));

  return (
    <div className="p-4 lg:p-6 space-y-5 lg:space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-slate-500">Passenger Cards · NCMC-style</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">Smart Transit Card</h1>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
          <button data-testid="transit-tc-btn" onClick={()=>setShowTerms(true)} className="px-3 py-2 border border-slate-200 rounded-md text-xs hover:bg-slate-50 inline-flex items-center gap-1 whitespace-nowrap"><BookOpen size={12}/>T&amp;C</button>
          <button data-testid="transit-faq-btn" onClick={()=>setShowFAQ(true)} className="px-3 py-2 border border-slate-200 rounded-md text-xs hover:bg-slate-50 inline-flex items-center gap-1 whitespace-nowrap"><Question size={12}/>FAQ</button>
          <button data-testid="issue-transit-card-btn" onClick={() => { setF(blank); setStep(1); setShow(true); }}
                  className="ktcl-btn-primary px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-semibold flex items-center gap-1.5 whitespace-nowrap">
            <Plus size={14}/><span className="hidden sm:inline">Issue Transit Card</span><span className="sm:hidden">Issue Card</span>
          </button>
        </div>
      </div>

      {/* Balance Lookup widget */}
      <div className="ktcl-card p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[280px]">
          <MagnifyingGlass size={18} className="text-slate-400"/>
          <input data-testid="transit-balance-input" value={balLookup.q}
                 onChange={e=>setBalLookup({...balLookup, q:e.target.value})}
                 placeholder="Check Card Balance — enter card no. (4100 …) or 10-digit mobile"
                 className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"/>
          <button data-testid="transit-balance-check" onClick={lookupBalance} disabled={balLookup.busy}
                  className="ktcl-btn-primary px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-60">
            {balLookup.busy ? "Checking…" : "Check Balance"}
          </button>
        </div>
        {balLookup.result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md px-4 py-2 text-sm flex items-center gap-4">
            <div><div className="text-[10px] text-slate-500 uppercase">Card</div><div className="font-mono font-bold text-[#1E3A8A]">{balLookup.result.card_no}</div></div>
            <div><div className="text-[10px] text-slate-500 uppercase">Holder</div><div className="font-semibold">{balLookup.result.holder_name}</div></div>
            <div><div className="text-[10px] text-slate-500 uppercase">Balance</div><div className="font-display font-bold text-2xl text-emerald-700">₹{balLookup.result.balance}</div></div>
            <div><div className="text-[10px] text-slate-500 uppercase">Status</div><div className="font-semibold">{balLookup.result.status}</div></div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
        {counts.map(({ cat, count }) => (
          <button key={cat} onClick={() => { setF({ ...blank, category: cat }); setStep(1); setShow(true); }}
                  className="ktcl-card ktcl-card-hover p-4 text-left hover:border-[#1E3A8A] transition-all">
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-semibold text-slate-800 leading-snug">{cat}</div>
              <div className="font-display text-2xl font-bold text-[#1E3A8A] tabular-nums shrink-0">{count}</div>
            </div>
            <div className="text-xs text-slate-500 mt-2">Register new →</div>
          </button>
        ))}
      </div>

      <div className="ktcl-card overflow-x-auto scroll-thin">
        <div className="p-3 lg:p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-display font-bold text-base lg:text-lg">Issued Transit Cards</h3>
          <input data-testid="transit-card-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search card no / name / mobile"
                 className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none w-full sm:w-72"/>
        </div>
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Card No</th>
              <th className="px-3 py-2 text-left">Holder</th>
              <th className="px-3 py-2 text-left">Category</th>
              <th className="px-3 py-2 text-left">Mobile</th>
              <th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2 text-left">Counter</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map(c => (
              <tr key={c.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-mono font-bold text-[#1E3A8A]">{c.card_no}</td>
                <td className="px-3 py-2">{c.name}</td>
                <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{c.category}</span></td>
                <td className="px-3 py-2 font-mono text-xs">{c.mobile}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">₹{c.balance ?? 0}</td>
                <td className="px-3 py-2 text-xs">{c.counter}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${c.status==="Active"?"bg-green-50 text-green-700":"bg-slate-100 text-slate-500"}`}>{c.status}</span>
                </td>
                <td className="px-3 py-2 text-right space-x-1">
                  <button onClick={()=>printReceipt({ kind: "card", data: { ...c, holder_name: c.name, card_type: c.category, deposit: c.card_fee, amount: c.opening_balance } })}
                          className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-100 inline-flex items-center gap-1"><Printer size={11}/>Print</button>
                  <button data-testid={`transit-passbook-${c.id}`} onClick={()=>setPassbookCard(c)}
                          className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-100 inline-flex items-center gap-1"><Receipt size={11}/>Passbook</button>
                  {c.status === "Active" && (
                    <button data-testid={`transit-topup-${c.id}`} onClick={()=>setTopupCard({ ...c, balance: c.balance ?? 0, _endpoint: "transit-cards" })}
                            className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-100 inline-flex items-center gap-1"><Money size={11}/>Top-up</button>
                  )}
                  {c.status === "Active" && (
                    <button data-testid={`transit-lost-${c.id}`} onClick={()=>setLostCard(c)}
                            className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 inline-flex items-center gap-1"><WarningCircle size={11}/>Lost</button>
                  )}
                  {c.status === "Active" && (
                    <button onClick={()=>cancelCard(c)} className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50">Cancel</button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500">No transit cards issued yet — click &ldquo;Issue Transit Card&rdquo;</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {topupCard && <CardTopupModal card={topupCard} onClose={()=>setTopupCard(null)} onDone={load}/>}
      {passbookCard && <PassbookModal kind="transit-card" item={passbookCard} onClose={()=>setPassbookCard(null)}/>}
      {lostCard && <ReportLostModal kind="transit-card" item={lostCard} onClose={()=>setLostCard(null)} onDone={load}/>}
      {showTerms && <InfoModal title="Smart Transit Card · Terms & Conditions" kind="transit-card-terms" fallbackSections={TRANSIT_CARD_TERMS} onClose={()=>setShowTerms(false)} icon={<BookOpen size={18}/>}/>}
      {showFAQ && <InfoModal title="Smart Transit Card · FAQ" kind="transit-card-faq" fallbackSections={TRANSIT_CARD_FAQ} onClose={()=>setShowFAQ(false)} icon={<Question size={18}/>}/>}

      {show && (
        <div className="fixed inset-0 bg-black/50 z-[1300] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={()=>setShow(false)}>
          <div data-testid="transit-card-modal" className="bg-white w-full max-w-3xl max-h-[90dvh] sm:max-h-[92vh] overflow-auto rounded-t-2xl sm:rounded-lg" onClick={e=>e.stopPropagation()}>
            <div className="sticky top-0 bg-white p-5 border-b border-slate-200 flex items-start justify-between z-10">
              <div>
                <h3 className="font-display text-xl font-bold">{f.category} Card Registration Form</h3>
                <div className="text-xs text-slate-500 mt-0.5">Step {step} of 3</div>
              </div>
              <button onClick={()=>setShow(false)} className="text-slate-400 hover:text-slate-700"><X size={20}/></button>
            </div>
            <div className="p-5 space-y-4">
              {step === 1 && (
                <>
                  <Section title="1. Card Category *">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {opts.categories.map(c => (
                        <button key={c} data-testid={`category-${c.replace(/ /g,'-')}`}
                                onClick={()=>setF({...f, category:c})}
                                className={`px-3 py-3 border rounded-md text-sm leading-snug text-left transition-colors ${f.category===c?"border-[#1E3A8A] bg-blue-50 font-semibold text-[#1E3A8A]":"border-slate-200 hover:bg-slate-50"}`}>
                          {c}
                        </button>
                      ))}
                    </div>
                  </Section>
                  <Section title="2. Basic Details *">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Name *"><Inp v={f.name} on={v=>setF({...f, name:v})} placeholder="Full Name"/></Field>
                      <Field label="Father Name / Husband Name *"><Inp v={f.parent_name} on={v=>setF({...f, parent_name:v})} placeholder="Father / Husband Name"/></Field>
                      <Field label="Government ID Type *">
                        <Sel v={f.govt_id_type} on={v=>setF({...f, govt_id_type:v})} options={opts.govt_id_types}/>
                      </Field>
                      <Field label="Government ID Number *"><Inp v={f.govt_id_number} on={v=>setF({...f, govt_id_number:v})} placeholder="Aadhar number / Driving Licence / Any govt card no."/></Field>
                      <Field label="Mobile Number *"><Inp v={f.mobile} on={v=>setF({...f, mobile:v})} placeholder="10-digit mobile"/></Field>
                      <Field label="Email"><Inp v={f.email} on={v=>setF({...f, email:v})} placeholder="Enter Email Address"/></Field>
                    </div>
                  </Section>
                </>
              )}

              {step === 2 && (
                <>
                  <Section title="Personal Information *">
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="Date of Birth *"><Inp type="date" v={f.date_of_birth} on={v=>setF({...f, date_of_birth:v})}/></Field>
                      <Field label="Age"><Inp type="number" v={f.age} on={v=>setF({...f, age:v})} placeholder="Auto from DOB"/></Field>
                      <Field label="Gender *">
                        <Sel v={f.gender} on={v=>setF({...f, gender:v})} options={GENDERS} placeholder="Select"/>
                      </Field>
                    </div>
                  </Section>
                  <Section title="Communication Address *">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Address *"><Inp v={f.address} on={v=>setF({...f, address:v})} placeholder="House / Street / Locality"/></Field>
                      <Field label="City / Village *"><Inp v={f.city} on={v=>setF({...f, city:v})} placeholder="Enter City Name"/></Field>
                      <Field label="State *"><Inp v={f.state} on={v=>setF({...f, state:v})}/></Field>
                      <Field label="Pincode *"><Inp v={f.pincode} on={v=>setF({...f, pincode:v})} placeholder="6-digit pincode"/></Field>
                    </div>
                  </Section>
                  {f.category === "Student" && (
                    <Section title="Institution Details *">
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Institution Type *">
                          <Sel v={f.institution_type} on={v=>setF({...f, institution_type:v})} options={opts.institution_types} placeholder="Select Institution Type"/>
                        </Field>
                        <Field label="Institution Name *">
                          <Sel v={opts.institutions.includes(f.institution_name) ? f.institution_name : (f.institution_name ? "Other" : "")}
                               on={v=>setF({...f, institution_name: v === "Other" ? "" : v})}
                               options={opts.institutions} placeholder="Select Institute"/>
                        </Field>
                        {(!opts.institutions.includes(f.institution_name) || f.institution_name === "") && (
                          <Field label="Other Institution Name *">
                            <Inp v={f.institution_name} on={v=>setF({...f, institution_name:v})} placeholder="Enter Institution Name"/>
                          </Field>
                        )}
                        <Field label="Expiry date as per ID card *">
                          <Inp type="date" v={f.institution_id_expiry} on={v=>setF({...f, institution_id_expiry:v})}/>
                        </Field>
                      </div>
                    </Section>
                  )}
                </>
              )}

              {step === 3 && (
                <>
                  <Section title="Upload Files">
                    <div className="text-[11px] text-slate-500 mb-2">Note: File should not be more than 5MB, and the file type should be JPG, JPEG, PNG, or PDF only.</div>
                    <div className="grid grid-cols-2 gap-3">
                      <Upload label="Government ID Photo *" url={f.govt_id_url} loading={uploading==="govt_id_url"} onUpload={(file)=>uploadFile("govt_id_url", file)}/>
                      <Upload label="Passport-size Photograph *" url={f.photo_url} loading={uploading==="photo_url"} onUpload={(file)=>uploadFile("photo_url", file)}/>
                      {categoryDocs.extras.map(d => (
                        <Upload key={d.field} label={d.label} url={f[d.field]} loading={uploading===d.field} onUpload={(file)=>uploadFile(d.field, file)}/>
                      ))}
                    </div>
                  </Section>
                  <Section title="Card Charges">
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-4 space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs text-slate-500">Registration Fee (one-time)</div>
                          <div className="text-[10px] text-emerald-600 italic">✓ Full ₹{f.card_fee} credited back as wallet balance</div>
                        </div>
                        <div className="font-bold text-base tabular-nums">₹{f.card_fee}</div>
                      </div>
                      {opts.deposit_enabled && opts.deposit_amount > 0 && (
                        <div className="border-t border-slate-200 pt-3 flex items-center justify-between" data-testid="card-deposit-row">
                          <div>
                            <div className="text-xs text-slate-500">Refundable Deposit</div>
                            <div className="text-[10px] text-amber-700 italic">⌃ Refunded in full on card cancellation</div>
                          </div>
                          <div className="font-bold text-base tabular-nums text-amber-700">₹{opts.deposit_amount}</div>
                        </div>
                      )}
                      <div className="border-t border-slate-200 pt-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="text-xs text-slate-500">Extra Top-up at Issuance (optional)</div>
                            <div className="text-[10px] text-slate-400">If added, minimum ₹100 · Maximum ₹10,000</div>
                          </div>
                          <input type="number" min={0} value={f.opening_balance}
                                 onChange={e=>setF({...f, opening_balance: +e.target.value})}
                                 className="w-28 px-2 py-1 bg-white border border-slate-200 rounded text-base font-bold outline-none text-right"/>
                        </div>
                      </div>
                      <div className="border-t border-slate-200 pt-3 grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-xs text-slate-500">Total Payable</div>
                          <div className="font-display font-bold text-2xl text-[#1E3A8A]" data-testid="card-total-payable">
                            ₹{(+f.card_fee || 0) + (+f.opening_balance || 0) + (opts.deposit_enabled ? (+opts.deposit_amount || 0) : 0)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500">Card Balance at Issuance</div>
                          <div className="font-display font-bold text-2xl text-emerald-700">₹{(+f.card_fee || 0) + (+f.opening_balance || 0)}</div>
                        </div>
                      </div>
                    </div>
                  </Section>
                  <Section title="Select Counter *">
                    <Sel v={f.counter} on={v=>setF({...f, counter:v})} options={opts.counters} placeholder="— Select counter —"/>
                  </Section>
                  <Section title="Self Declaration *">
                    <label className="flex gap-3 p-3 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={f.consent_signed} onChange={e=>setF({...f, consent_signed:e.target.checked})} className="mt-1"/>
                      <span className="text-xs leading-relaxed text-slate-700">
                        I hereby confirm that the data submitted in the above form is accurate and complete to the best of my knowledge. I understand that any false information may lead to the rejection of my application.
                      </span>
                    </label>
                  </Section>
                </>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex items-center justify-between">
              <div className="text-sm text-slate-500">
                {step === 3 && <>Total: <span className="font-display font-bold text-lg text-[#1E3A8A]">₹{(+f.card_fee || 0) + (+f.opening_balance || 0) + (opts.deposit_enabled ? (+opts.deposit_amount || 0) : 0)}</span></>}
              </div>
              <div className="flex gap-2">
                {step > 1 && <button onClick={()=>setStep(step-1)} className="px-4 py-2 border border-slate-200 rounded-md text-sm">← Back</button>}
                {step < 3 && <button data-testid="transit-card-next" onClick={()=>setStep(step+1)} className="ktcl-btn-primary px-5 py-2 rounded-md text-sm font-semibold">Next →</button>}
                {step === 3 && <button data-testid="transit-card-issue" onClick={issue} className="ktcl-btn-primary px-5 py-2 rounded-md text-sm font-semibold">Issue Card · ₹{(+f.card_fee || 0) + (+f.opening_balance || 0) + (opts.deposit_enabled ? (+opts.deposit_amount || 0) : 0)}</button>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="label-cap text-slate-500 mb-2">{title}</div>
    {children}
  </div>
);
const Field = ({ label, children }) => (
  <div>
    <div className="text-[11px] uppercase text-slate-500 mb-1 font-semibold">{label}</div>
    {children}
  </div>
);
const Inp = ({ v, on, type="text", placeholder="" }) => (
  <input type={type} value={v} placeholder={placeholder} onChange={(e)=>on(e.target.value)}
         className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:border-[#1E3A8A]"/>
);
const Sel = ({ v, on, options, placeholder }) => (
  <select value={v} onChange={(e)=>on(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);
const Upload = ({ label, url, loading, onUpload }) => (
  <label className={`flex items-center gap-2 p-3 border-2 border-dashed rounded-md cursor-pointer transition-colors ${url?"border-green-300 bg-green-50":"border-slate-200 hover:border-[#1E3A8A] hover:bg-blue-50"}`}>
    {url ? <FileText size={18} className="text-green-600"/> : <UploadSimple size={18} className="text-slate-400"/>}
    <div className="flex-1 min-w-0">
      <div className="text-xs font-semibold text-slate-700 truncate">{label}</div>
      <div className="text-[10px] text-slate-500 truncate">{loading?"Uploading…":(url?"✓ Uploaded":"Click to upload")}</div>
    </div>
    <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={e=>onUpload(e.target.files[0])}/>
  </label>
);
